import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/services/firebase';
import {
  NOTIFICATION_LIST_LIMIT,
  NOTIFICATIONS_COLLECTION,
} from '@/services/notifications/constants';
import { sanitizeNotificationMetadata } from '@/services/notifications/sanitizeMetadata';
import type {
  Notification,
  NotificationFilters,
  NotificationInput,
} from '@/types/notification';

type NotificationDoc = {
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  priority: string;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  readAt?: string;
};

function mapNotification(id: string, data: NotificationDoc): Notification {
  const base: Notification = {
    id,
    userId: data.userId,
    type: data.type as Notification['type'],
    title: data.title,
    message: data.message,
    read: data.read === true,
    priority: (data.priority as Notification['priority']) || 'medium',
    createdAt: data.createdAt,
  };
  if (data.entityType) base.entityType = data.entityType;
  if (data.entityId) base.entityId = data.entityId;
  if (data.metadata) base.metadata = data.metadata;
  if (data.readAt) base.readAt = data.readAt;
  return base;
}

export async function insertNotification(
  input: NotificationInput,
): Promise<Notification> {
  const now = new Date().toISOString();
  const meta = sanitizeNotificationMetadata(input.metadata);
  const payload: NotificationDoc = {
    userId: input.userId,
    type: input.type,
    title: input.title.trim(),
    message: input.message.trim(),
    read: false,
    priority: input.priority ?? 'medium',
    createdAt: now,
  };
  if (input.entityType) payload.entityType = input.entityType;
  if (input.entityId) payload.entityId = input.entityId;
  if (meta) payload.metadata = meta;

  const ref = await addDoc(
    collection(getFirestoreDb(), NOTIFICATIONS_COLLECTION),
    payload,
  );
  return mapNotification(ref.id, payload);
}

export async function fetchUserNotifications(
  userId: string,
  filters: NotificationFilters = {},
  maxItems = NOTIFICATION_LIST_LIMIT,
): Promise<Notification[]> {
  const constraints = [
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(maxItems),
  ];

  const snap = await getDocs(
    query(collection(getFirestoreDb(), NOTIFICATIONS_COLLECTION), ...constraints),
  );

  let items = snap.docs.map((d) =>
    mapNotification(d.id, d.data() as NotificationDoc),
  );

  if (filters.read !== undefined) {
    items = items.filter((n) => n.read === filters.read);
  }
  if (filters.type) {
    items = items.filter((n) => n.type === filters.type);
  }
  if (filters.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    items = items.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        n.message.toLowerCase().includes(q),
    );
  }

  return items;
}

export async function markNotificationRead(
  notificationId: string,
  userId: string,
): Promise<void> {
  await updateDoc(
    doc(getFirestoreDb(), NOTIFICATIONS_COLLECTION, notificationId),
    {
      read: true,
      readAt: new Date().toISOString(),
    },
  );
  void userId;
}

export async function markAllNotificationsRead(userId: string): Promise<number> {
  const unread = await fetchUserNotifications(
    userId,
    { read: false },
    NOTIFICATION_LIST_LIMIT,
  );
  if (unread.length === 0) return 0;

  const batch = writeBatch(getFirestoreDb());
  const now = new Date().toISOString();
  for (const n of unread) {
    batch.update(doc(getFirestoreDb(), NOTIFICATIONS_COLLECTION, n.id), {
      read: true,
      readAt: now,
    });
  }
  await batch.commit();
  return unread.length;
}

export async function countUnreadNotifications(userId: string): Promise<number> {
  const items = await fetchUserNotifications(userId, { read: false }, 100);
  return items.length;
}
